import { Alert, Box, Field, Text } from '@chakra-ui/react';
import {
  ugcVideoUnits,
  type BriefFixedPayment,
  type BriefTemplateContent,
  type CreatorUgcOffer,
} from '@fluenceur/types';
import { useTranslation } from 'react-i18next';
import { formatCents } from '@/libs/utils/format-money';
import { CollabCompensationEstimate } from '@/pages/collabs/brief-templates/_shared/CollabCostEstimate';
import { MoneyAmountField } from '@/pages/collabs/brief-templates/editor/sections/MoneyAmountField';
import { isBelowUgcStartingPrice } from './ugc-starting-price';

interface CollabTermsUgcCompensationProps {
  content: BriefTemplateContent;
  updateContent: (updater: (prev: BriefTemplateContent) => BriefTemplateContent) => void;
  /** The single recipient's name and UGC offer; null in bulk or for a creator who states none. */
  creator: { name: string; offer: CreatorUgcOffer } | null;
  showAmountError: boolean;
}

/**
 * The fixed payment of a UGC proposal. A UGC is always paid, so there is no
 * type to pick, and the product the brief adds stays beside the amount. The
 * creator's starting price per video sits next to it as a reference, and an
 * amount below it for the videos asked is said softly: the brand may still send.
 */
export const CollabTermsUgcCompensation = ({
  content,
  updateContent,
  creator,
  showAmountError,
}: CollabTermsUgcCompensationProps) => {
  const { t, i18n } = useTranslation();
  const { compensation } = content;

  const setFixedPayment = (fixedPayment: BriefFixedPayment | null) =>
    updateContent((prev) => ({
      ...prev,
      compensation: { ...prev.compensation, type: 'fixed_payment', fixedPayment },
    }));

  const price = creator
    ? formatCents(creator.offer.startingPriceCents, creator.offer.currency, i18n.language)
    : null;
  // Compared only in the same currency: a price in pounds says nothing against
  // one in euros, so the reference then stands alone.
  const below =
    !!creator &&
    compensation.fixedPayment?.currency === creator.offer.currency &&
    isBelowUgcStartingPrice(
      compensation.fixedPayment?.amountCents,
      ugcVideoUnits(content.deliverables),
      creator.offer.startingPriceCents
    );

  return (
    <Field.Root>
      <Field.Label fontFamily="heading" fontSize="12.5px" fontWeight="semibold">
        {t('collabs.briefTemplates.sections.compensation.title')}
      </Field.Label>
      <Box w="full">
        <MoneyAmountField
          value={compensation.fixedPayment}
          onChange={setFixedPayment}
          hasError={showAmountError}
        />
        {creator && price && (
          <Text fontSize="11.5px" color="fg.muted" mt="1.5">
            {t('invitations.modal.collab.wizard.terms.ugc.startingPrice', {
              name: creator.name,
              price,
            })}
          </Text>
        )}
        {below && creator && price && (
          <Alert.Root status="warning" mt="2" size="sm">
            <Alert.Indicator />
            <Alert.Title fontWeight="normal">
              {t('invitations.modal.collab.wizard.terms.ugc.belowStartingPrice', {
                name: creator.name,
                price,
              })}
            </Alert.Title>
          </Alert.Root>
        )}
        <Box mt="2.5">
          <CollabCompensationEstimate compensation={compensation} />
        </Box>
      </Box>
    </Field.Root>
  );
};
